<template>
  <v-col class="ml-3 mt-1 flex-nowrap">
    <div class="cardText mr-2 mb-3 mt-n1">
      <span>
        <!-- eslint-disable vue/singleline-html-element-content-newline -->
        <span style="white-space: pre-wrap; overflow-wrap: anywhere;">{{ item.text }}</span>
        <span class="timestamp">
          {{ title }} {{ formatTimestamp(item.timestamp) }}
        </span>
      </span>
      <a
        v-if="!readOnly"
        class="deleteButton pl-1"
        @click="archiveElement"
      >
        {{ $t('actions.delete') }}
      </a>
    </div>
    <div
      v-for="comment in firstThreeThreadComments(item.id)"
      :key="comment.id"
    >
      <v-row no-gutters class="my-2 flex-nowrap">
        <span class="votesCounters">
          <span class="votesCountersInside">
            {{ comment.upVotes ? comment.upVotes : "" }}
          </span>
        </span>
        <v-icon
          :class="{
            'upVoteSet': isCommentUpVoteSet(comment.id),
            'votesIcons align-self-top mt-1': true,
          }"
          @click="threadUpVoteClick(comment.id)"
        >
          arrow_drop_up
        </v-icon>
        <div class="d-flex flex-nowrap threadCommentDiv">
          <span class="ml-2 commentText">
            <span> {{ comment.text }} </span>
            <span class="timestamp">
              {{ getUserName(comment.auditInfo.modifiedBy) }}
              {{ formatTimestamp(comment.timestamp) }}
            </span>
            <a
              v-if="isThreadCommentOwnedByCurrentUser(comment.auditInfo.modifiedBy)"
              class="deleteButton pl-1"
              @click="archiveThreadComment(comment.id)"
            >
              {{ $t('actions.delete') }}
            </a>
          </span>
        </div>
      </v-row>
    </div>
    <a
      v-if="panel[item.id] === undefined && visibleThreadComments(item.id).length > 3"
      class="addCommentBtn"
      @click="expandComments(item.id)"
    >
      {{ generateMoreCommentsString(visibleThreadComments(item.id).length - 3) }}
    </a>
    <v-expansion-panels flat v-model="panel[item.id]">
      <v-expansion-panel>
        <v-expansion-panel-content class="panelContent">
          <div
            v-for="comment in remainingThreadComments(item.id)"
            :key="comment.id"
          >
            <v-row no-gutters class="mb-2 flex-nowrap">
              <span class="votesCounters">
                <span class="votesCountersInside">
                  {{ comment.upVotes ? comment.upVotes : "" }}
                </span>
              </span>
              <v-icon
                :class="{
                  'upVoteSet': isCommentUpVoteSet(comment.id),
                  'votesIcons align-self-top mt-1': true,
                }"
                @click="threadUpVoteClick(comment.id)"
              >
                arrow_drop_up
              </v-icon>
              <div class="d-flex flex-nowrap threadCommentDiv">
                <span class="ml-2 commentText">
                  <span> {{ comment.text }} -</span>
                  <span class="timestamp">
                    {{ getUserName(comment.auditInfo.modifiedBy) }}
                    {{ formatTimestamp(comment.timestamp) }}
                  </span>

                  <a
                    v-if="isThreadCommentOwnedByCurrentUser(comment.auditInfo.modifiedBy)"
                    class="deleteButton pl-1"
                    @click="archiveThreadComment(comment.id)"
                  >
                    {{ $t('actions.delete') }}
                  </a>
                </span>
              </div>
            </v-row>
          </div>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
    <a
      v-if="panel[item.id] === 0 && visibleThreadComments(item.id).length > 3"
      :class="{
        'pt-n4 mr-2': true,
        'addCommentBtn': true,
      }"
      @click="shrinkComments(item.id)"
    >
      {{ generateHideCommentsString(visibleThreadComments(item.id).length - 3) }}
    </a>
    <a
      v-if="panel[item.id] === 0 || visibleThreadComments(item.id).length <= 3"
      :class="{
        'pt-n4': true,
        'addCommentBtn': true,
        'highlightLink': currentlyAddingThreadComment[item.id] !== undefined,
      }"
      @click="addThreadCommentClicked(item)"
    >
      {{ $t('actions.addReply') }}
    </a>
    <v-expand-transition>
      <TextInput
        v-if="isEditingComment(item.id)"
        ref="textInput"
        class="mt-2"
        :element-id="item.id"
        :placeholder="$t('extension.userAnalyses.comments.reply.placeholder')"
        @save="saveNewThreadComment"
        @cancel="cancelNewThreadComment"
      />
    </v-expand-transition>
  </v-col>
</template>
<script>
import Vue from 'vue';
import moment from 'moment';
import { mapGetters, mapActions } from 'vuex';
import TextInput from './mission/TextInput';
import { types as analysesTypes } from '../../store/analyses';
import { types as userTypes } from '../../store/user';
import { types as usersTypes } from '../../store/users';

export default {
  name: 'AnalysisThread',
  components: {
    TextInput,
  },
  props: {
    title: {
      type: String,
      required: true,
    },
    analysisId: {
      type: String,
      required: true,
    },
    item: {
      type: Object,
      required: true,
    },
    readOnly: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      panel: [],
      currentlyAddingThreadComment: [],
    };
  },
  computed: {
    ...mapGetters({
      currentAnalysisComments: analysesTypes.getters.currentAnalysisComments,
      userDetails: userTypes.getters.userDetails,
      usersProfiles: usersTypes.getters.usersProfiles,
    }),
  },
  methods: {
    ...mapActions({
      toggleUserCommentUpvote: analysesTypes.actions.toggleUserCommentUpvote,
      saveThreadComment: analysesTypes.actions.saveThreadComment,
      getUserProfile: usersTypes.actions.getUserProfile,
    }),
    visibleThreadComments(commentId) {
      const visibleThreadComments = this.threadComments(commentId)
        .filter(comment => !comment.archived || comment.archived === false);

      return visibleThreadComments;
    },
    firstThreeThreadComments(commentId) {
      const visibleThreadComments = this.visibleThreadComments(commentId);
      return visibleThreadComments.slice(0, 3);
    },
    remainingThreadComments(commentId) {
      const visibleThreadComments = this.visibleThreadComments(commentId);
      return visibleThreadComments.slice(3, visibleThreadComments.length);
    },
    threadComments(commentId) {
      return this.currentAnalysisComments.filter(comment =>
        comment.refId === commentId).sort((a, b) => a.timestamp - b.timestamp);
    },
    isCommentUpVoteSet(commentId) {
      const commentIndex = this.currentAnalysisComments.findIndex(comment => comment.id === commentId);
      const { votes } = this.currentAnalysisComments[commentIndex];
      if (!votes) return false;

      const voteIndex = votes.findIndex(vote => vote.id === this.userDetails.userId);
      return voteIndex !== -1 && votes[voteIndex].upVoteValue;
    },
    threadUpVoteClick(commentId) {
      this.toggleUserCommentUpvote({ commentId, userId: this.userDetails.userId });
    },
    expandComments(elementId) {
      Vue.set(this.panel, elementId, 0);
    },
    shrinkComments(elementId) {
      Vue.set(this.panel, elementId, undefined);
      this.cancelNewThreadComment({ elementId });
    },
    cancelNewThreadComment({ elementId }) {
      Vue.set(this.currentlyAddingThreadComment, elementId, undefined);
    },
    saveNewThreadComment({ text, elementId }) {
      if (this.currentlyAddingThreadComment[elementId] !== undefined) {
        // const isSigElem = this.currentlyAddingThreadComment[elementId].hasOwnProperty('signification');
        const isSigElem = Object.prototype.hasOwnProperty.call(this.currentlyAddingThreadComment[elementId], 'signification');
        const comment = {
          refType: isSigElem ? 'SIG_ELEM' : 'CODE_COMMENT',
          text,
          refId: elementId,
        };

        this.currentlyAddingThreadComment[elementId] = undefined;
        this.saveThreadComment({ comment, analysisId: this.analysisId });
      }
    },
    addThreadCommentClicked(item) {
      if (this.currentlyAddingThreadComment[item.id] === undefined) {
        this.focusNewComment(item);
      } else {
        Vue.set(this.currentlyAddingThreadComment, item.id, undefined);
      }
    },
    focusNewComment(item = this.item) {
      Vue.set(this.currentlyAddingThreadComment, item.id, item);
    },
    isEditingComment(id) {
      const isEditing = this.currentlyAddingThreadComment[id] !== undefined;
      if (isEditing) {
        this.$nextTick(() => this.$refs.textInput.$refs.textArea.focus());
      }
      return isEditing;
    },
    formatTimestamp(timestamp) {
      return timestamp ? moment(timestamp).fromNow() : '';
    },
    getUserName(userId) {
      // TODO Check if this is ok...?? Copied from elsewhere in code, not sure at all
      //      it's the right way to do things... (nothing ensures the profile will really
      //      be loaded in time...? If I make this function async, it doesn't render in the
      //      template, it renders "[object Promise]"
      this.getUserProfile(userId);
      const userProfile = this.usersProfiles[userId];
      return userProfile ? `${userProfile.firstName} ${userProfile.lastName}` : '';
    },
    isThreadCommentOwnedByCurrentUser(commentOwnerUserId) {
      return commentOwnerUserId === this.userDetails.userId;
    },
    archiveElement() {
      this.$emit('archiveElement');
    },
    archiveThreadComment(commentId) {
      this.$emit('archiveThreadComment', commentId);
    },
    generateMoreCommentsString(nbComments) {
      return this.$tc('extension.userAnalyses.comments.showMore', nbComments, { nbComments });
    },
    generateHideCommentsString(nbComments) {
      return this.$tc('extension.userAnalyses.comments.hide', nbComments, { nbComments });
    },
  },
};
</script>

<style scoped>
.cardText {
  font-size: 13px;
  min-height: 40px;
}
.votesCounters {
  font-size: 11px;
  /* width: 30px;*/
  min-width: 30px;
  max-width: 30px;
  text-align: end;
  position: relative;
}
.votesCountersInside {
  position: absolute;
  top: 3px;
  left: -2px;
  width: 30px;
}
.upVoteSet {
  color: #06D6A0;
}
.commentText {
  font-size: 13px;
  line-height: 1.5;
  overflow-wrap: anywhere;
}
.cardText:hover .deleteButton {
  visibility: visible;
}
.threadCommentDiv:hover .deleteButton {
  visibility: visible;
}
.deleteButton {
  visibility: hidden;
  color: red;
  font-size: 10px;
}
.addCommentBtn {
  font-size: 11px;
  color: #848d95;
}
.addCommentBtn:hover {
  color: #3F47CD;
}
.panelContent {
  margin-left: -24px;
}
.timestamp {
  font-size: 11px;
  color: #848d95;
}
.highlightLink {
  color: #3F47CD !important;
}
.highlightLink span {
  color: orange;
  outline: 2px solid red;
}
.votesIcons {
  width: 16px;
  height: 16px;
  color: default;
}

.votesIcons:hover {
  color: #06D6A0;
}

.votesIcons:focus::after {
  opacity: 0;
}
</style>
